import "./FinanceFilterPills.scss";

import Tooltip from "components/Tooltip/Tooltip";
import React from "react";

import { FILTER_CATEGORIES } from "../../constants";
import type { FinanceFilters } from "../../types";

type FinanceFilterPillsProps = {
  filters: FinanceFilters;
  onRemoveFilter: (filterKey: keyof FinanceFilters) => void;
};

// pills that appear in the Filter header
const FinanceFilterPills: React.FC<FinanceFilterPillsProps> = ({
  filters,
  onRemoveFilter,
}) => {
  const pills: Array<{
    key: keyof FinanceFilters;
    label: string;
    value: string;
    icon: string;
  }> = [];

  // posted dates
  if (filters.posted_date_start || filters.posted_date_end) {
    const value = [filters.posted_date_start, filters.posted_date_end]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "posted_date_start",
      label: "Posted Dates",
      value,
      icon: FILTER_CATEGORIES.posted_dates.icon,
    });
  }

  // service dates
  if (filters.service_date_from || filters.service_date_to) {
    const value = [filters.service_date_from, filters.service_date_to]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "service_date_from",
      label: "Service Dates",
      value,
      icon: FILTER_CATEGORIES.service_dates.icon,
    });
  }

  // clients
  if (filters.clients && filters.clients.length > 0) {
    pills.push({
      key: "clients",
      label: "Clients",
      value: filters.clients.map((c) => c.label).join(", "),
      icon: FILTER_CATEGORIES.clients.icon,
    });
  }

  // service types
  if (filters.service_types && filters.service_types.length > 0) {
    pills.push({
      key: "service_types",
      label: "Service Types",
      value: filters.service_types.map((st) => st.label).join(", "),
      icon: FILTER_CATEGORIES.service_types.icon,
    });
  }

  // file IDs
  if (filters.file_ids && filters.file_ids.length > 0) {
    pills.push({
      key: "file_ids",
      label: "Files",
      value: filters.file_ids.map((f) => f.label).join(", "),
      icon: FILTER_CATEGORIES.file_ids.icon,
    });
  }

  // invoice statuses
  if (filters.invoice_statuses && filters.invoice_statuses.length > 0) {
    pills.push({
      key: "invoice_statuses",
      label: "Invoice Status",
      value: filters.invoice_statuses.map((is) => is.label).join(", "),
      icon: FILTER_CATEGORIES.invoice_statuses.icon,
    });
  }

  // payment status
  if (filters.payment_status) {
    pills.push({
      key: "payment_status",
      label: "Payment",
      value: filters.payment_status.label,
      icon: FILTER_CATEGORIES.payment_status.icon,
    });
  }

  // invoice numbers
  if (filters.invoice_numbers && filters.invoice_numbers.length > 0) {
    pills.push({
      key: "invoice_numbers",
      label: "Invoice Numbers",
      value: filters.invoice_numbers.map((inv) => inv.label).join(", "),
      icon: FILTER_CATEGORIES.invoice_numbers.icon,
    });
  }

  // payment dates
  if (filters.payment_date_from || filters.payment_date_to) {
    const value = [filters.payment_date_from, filters.payment_date_to]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "payment_date_from",
      label: "Payment Dates",
      value,
      icon: FILTER_CATEGORIES.payment_dates.icon,
    });
  }

  // service created dates
  if (filters.service_created_from || filters.service_created_to) {
    const value = [filters.service_created_from, filters.service_created_to]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "service_created_from",
      label: "Service Created",
      value,
      icon: FILTER_CATEGORIES.service_created_dates.icon,
    });
  }

  // file opened dates
  if (filters.file_opened_on_from || filters.file_opened_on_to) {
    const value = [filters.file_opened_on_from, filters.file_opened_on_to]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "file_opened_on_from",
      label: "File Opened",
      value,
      icon: FILTER_CATEGORIES.file_opened_dates.icon,
    });
  }

  // ICN
  if (filters.icn) {
    pills.push({
      key: "icn",
      label: "ICN",
      value: filters.icn,
      icon: FILTER_CATEGORIES.icn.icon,
    });
  }

  // Claim ID
  if (filters.claim_id) {
    pills.push({
      key: "claim_id",
      label: "Claim ID",
      value: filters.claim_id,
      icon: FILTER_CATEGORIES.claim_id.icon,
    });
  }

  // Claim Number
  if (filters.claim_number) {
    pills.push({
      key: "claim_number",
      label: "Claim Number",
      value: filters.claim_number,
      icon: FILTER_CATEGORIES.claim_number.icon,
    });
  }

  // RRE Name
  if (filters.rre_name) {
    pills.push({
      key: "rre_name",
      label: "RRE Name",
      value: filters.rre_name,
      icon: FILTER_CATEGORIES.rre_name.icon,
    });
  }

  // RRE ID
  if (filters.rre_id) {
    pills.push({
      key: "rre_id",
      label: "RRE ID",
      value: filters.rre_id,
      icon: FILTER_CATEGORIES.rre_id.icon,
    });
  }

  // S111 Claimant Name
  if (filters.s111_claimant_name) {
    pills.push({
      key: "s111_claimant_name",
      label: "S111 Claimant",
      value: filters.s111_claimant_name,
      icon: FILTER_CATEGORIES.s111_claimant_name.icon,
    });
  }

  // Section 111: RRE
  if (filters.rre) {
    pills.push({
      key: "rre",
      label: "RRE",
      value: filters.rre.label,
      icon: FILTER_CATEGORIES.s111_rre.icon,
    });
  }

  // Section 111: File Type
  if (filters.file_type) {
    pills.push({
      key: "file_type",
      label: "File Type",
      value: filters.file_type.label,
      icon: FILTER_CATEGORIES.s111_file_type.icon,
    });
  }

  // Section 111: Send Dates
  if (filters.send_date_from || filters.send_date_to) {
    const value = [filters.send_date_from, filters.send_date_to]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "send_date_from",
      label: "Send Dates",
      value,
      icon: FILTER_CATEGORIES.s111_send_dates.icon,
    });
  }

  // Section 111: Action Filter
  if (filters.action_filter) {
    pills.push({
      key: "action_filter",
      label: "Action",
      value: filters.action_filter.label,
      icon: FILTER_CATEGORIES.s111_action_filter.icon,
    });
  }

  // Section 111: NSA Filter
  if (filters.nsa_filter) {
    pills.push({
      key: "nsa_filter",
      label: "NSA",
      value: String(filters.nsa_filter.value),
      icon: FILTER_CATEGORIES.s111_nsa_filter.icon,
    });
  }

  // Section 111: DOI
  if (filters.doi_from || filters.doi_to) {
    const value = [filters.doi_from, filters.doi_to]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "doi_from",
      label: "DOI",
      value,
      icon: FILTER_CATEGORIES.s111_doi_dates.icon,
    });
  }

  // Section 111: TPOC Date
  if (filters.tpoc_from || filters.tpoc_to) {
    const value = [filters.tpoc_from, filters.tpoc_to]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "tpoc_from",
      label: "TPOC Date",
      value,
      icon: FILTER_CATEGORIES.s111_tpoc_dates.icon,
    });
  }

  // Section 111: Medicare Entitlement Date
  if (filters.medicare_from || filters.medicare_to) {
    const value = [filters.medicare_from, filters.medicare_to]
      .filter(Boolean)
      .join(" to ");
    pills.push({
      key: "medicare_from",
      label: "Medicare Entitlement",
      value,
      icon: FILTER_CATEGORIES.s111_medicare_dates.icon,
    });
  }

  // no pills to show
  if (pills.length === 0) {
    return null;
  }

  return (
    <div className="finance-filter-pills">
      {pills.map((pill) => {
        const tooltipContent = `<strong>${pill.label}:</strong><br/>${pill.value}`;

        return (
          <Tooltip
            key={pill.key}
            content={tooltipContent}
            html={true}
            place="bottom"
            delayShow={200}
          >
            <div className="filter-pill">
              <span className="material-icons pill-icon">{pill.icon}</span>
              <span className="pill-label">{pill.label}:</span>
              <span className="pill-value">{pill.value}</span>
              <button
                className="pill-remove"
                onClick={() => onRemoveFilter(pill.key)}
                type="button"
                aria-label={`Remove ${pill.label} filter`}
              >
                <span className="material-icons">close</span>
              </button>
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default FinanceFilterPills;
